import { featureFlags } from "@kaggle/sdk";
import { ThemeName } from "@kaggle/styled-components";
import { getLogger } from "../api/getServices";
import { setKaggleIcon, setRunningIcon } from "../utils/favicon";
import {
  addMessageListener,
  handshakeAsHost,
  removeMessageListener,
} from "./iframe-bridge/messaging/transport";
import { analyticsEventIPC } from "./jupyter/ipc/analyticsEventIPC";
import {
  JupyterOutputAddedRequest,
  toOutputAdded,
} from "./jupyter/ipc/outputAddedIPC";
import { JupyterEvent } from "./jupyter/types";
import { editorFrameClient } from "./pages/editor/editorFrameClient";
import {
  editorErrorOccurred,
  editorKernelBecameBusy,
  editorKernelBecameIdle,
} from "./redux/actions/editor";
import { reportLifecycleChange } from "./redux/actions/lifecycle";
import { appendTerminal } from "./redux/actions/terminal";
import { getThemeName } from "./redux/selectors/theme";
import { AppStore } from "./redux/types";
import { LifecycleStage } from "./types/LifecycleLatencyStats";
import { Services } from "./types/Services";
import TerminalError from "./types/TerminalError";
import TerminalImage from "./types/TerminalImage";
import TerminalMessage from "./types/TerminalMessage";

// Setting busy should clear this timeout.
let setIdleTimeout = 0;

// TODO(herbison): Port analyticsEventIPC to Postmate.
type MessageHandler = (message: MessageEvent) => void;
let frameMessageHandler: MessageHandler | null = null;
export const init = (services: Services) => (store: AppStore) => {
  // List of handlers used to respond to Jupyter events triggered from iframe.
  const handlers = [
    analyticsEventIPC.makeHandler((_sourceWindow, _targetWindow, event) => {
      /*
       * We use IPC channels to report events from inside the iframe via the
       * app. This ensures page URL and session are identical between the two.
       */
      getLogger().report("kernels", event.eventType, event.data);
    }),
  ].map(handler =>
    handler(
      () => window,
      () => services.frameClient.getContentWindow()
    )
  );

  // TODO(herbison): Propagate these events from bootstrapJupyterLabSessionlessEditor.

  editorFrameClient.on(
    JupyterEvent.OUTPUT_ADDED,
    (req: JupyterOutputAddedRequest) => {
      const payload = toOutputAdded(req);
      const onOutputAdded = (text: string, type: string) => {
        if (type === "message") {
          appendTerminal([new TerminalMessage(text)]);
        }
        if (type === "image") {
          appendTerminal([new TerminalImage(text)]);
        }
      };
      const onOutputError = (error: {
        ename: string;
        evalue: string;
        traceback: string[];
      }) => {
        appendTerminal([
          new TerminalError(error.ename, error.evalue, error.traceback),
        ]);
      };

      switch (payload.type) {
        case "message":
          onOutputAdded(payload.value, "message");
          break;
        case "images":
          payload.values.forEach(value => {
            onOutputAdded(value, "image");
          });
          break;
        case "error":
          onOutputError(payload);
          break;
        default:
          return;
      }
    }
  );

  editorFrameClient.on(JupyterEvent.KERNEL_BUSY, () => {
    editorKernelBecameBusy();
    setRunningIcon();
    window.clearTimeout(setIdleTimeout);
  });

  editorFrameClient.on(JupyterEvent.KERNEL_IDLE, () => {
    window.clearTimeout(setIdleTimeout);
    setIdleTimeout = window.setTimeout(() => {
      editorKernelBecameIdle();
      setKaggleIcon();
    }, 500);
  });

  if (frameMessageHandler != null) {
    removeMessageListener(frameMessageHandler);
  }

  frameMessageHandler = (message: MessageEvent) => {
    handlers.forEach(handler => handler(message));
  };

  addMessageListener(frameMessageHandler);

  handshakeAsHost().then(
    () => {
      reportLifecycleChange(
        LifecycleStage.EDITOR_FRAME_COMMUNICATION_ESTABLISHED,
        Date.now()
      );
    },
    error => {
      editorErrorOccurred(
        "Error occurred while loading the notebook: " + error
      );
    }
  );

  // WARNING: Polling is not a good way to synchronize the theme,
  // but it seems to get things working reliably.
  let lastThemeName: ThemeName | undefined;
  window.setInterval(async () => {
    const themeName = getThemeName(store.getState());
    if (featureFlags.kernelEditorForceThemeSync) {
      if (themeName !== lastThemeName) {
        const jlFrameThemeName = await editorFrameClient.setTheme(themeName);
        // Make sure the JL frame has the desired theme before considering the
        // themes 'synced'.
        if (jlFrameThemeName === themeName) {
          lastThemeName = themeName;
        }
      }
      return;
    }

    if (themeName !== lastThemeName) {
      editorFrameClient.setTheme(themeName);
    }
    lastThemeName = themeName;
  }, 1000);
};
